import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookHeart, Truck, Clock, Check, RotateCcw, AlertCircle, Package, CalendarHeart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import client from "@/lib/api";
import { reportPaymentIssue } from "@/lib/pay";

// Each state gets a plain-English line and a colour, so the list can be read at
// a glance instead of decoded.
const STATE = {
    created:      { label: "Awaiting payment",    tone: "text-plum/50",    icon: Clock },
    awaiting_upi: { label: "Awaiting your UPI payment", tone: "text-plum/50", icon: Clock },
    upi_claimed:  { label: "Confirming payment",  tone: "text-bubblegum",  icon: Clock },
    paid:         { label: "Paid",                tone: "text-emerald-600", icon: Check },
    processing:   { label: "Being made",          tone: "text-bubblegum",  icon: BookHeart },
    printing:     { label: "At the printer",      tone: "text-bubblegum",  icon: BookHeart },
    packed:       { label: "Packed",              tone: "text-bubblegum",  icon: Package },
    shipped:      { label: "On its way",          tone: "text-bubblegum",  icon: Truck },
    delivered:    { label: "Delivered",           tone: "text-emerald-600", icon: Check },
    cancelled:    { label: "Cancelled",           tone: "text-plum/40",    icon: RotateCcw },
    refunded:     { label: "Refunded",            tone: "text-plum/40",    icon: RotateCcw },
    duplicate:    { label: "Refunding double payment", tone: "text-bubblegum", icon: RotateCcw },
};

// Books are drawn to order, so a confirmed order isn't something to hand back.
// The money we do return is money that never bought anything — so this is only
// offered on orders that never confirmed.
const canReportIssue = (o) =>
    !o.paid_at && !["refunded", "cancelled"].includes(o.status) && !o.payment_issue;

const when = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("en-IN",
            { day: "numeric", month: "short", year: "numeric" });
    } catch { return ""; }
};

export default function OrdersPanel() {
    const [orders, setOrders] = useState([]);
    const [busy, setBusy] = useState(null);

    const load = () => client.get("/orders")
        .then((r) => setOrders(r.data.orders || []))
        .catch(() => {});
    useEffect(() => { load(); }, []);

    const report = async (o) => {
        const reason = window.prompt(
            "Tell us what happened — and paste the UPI reference or bank reference if you have one.");
        if (reason === null) return;
        if (!reason.trim()) { toast.error("Tell us what happened so we can look it up"); return; }
        setBusy(o.id);
        try {
            await reportPaymentIssue(o.id, reason.trim().slice(0, 500));
            toast.success("Sent. We'll check our account and email you within a working day.");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send that");
        } finally { setBusy(null); }
    };

    if (!orders.length) return null;

    return (
        <section className="mb-14" data-testid="orders-panel">
            <div className="flex items-baseline justify-between mb-4">
                <div className="font-display text-2xl">Your orders</div>
                <Link to="/refund-policy" className="text-xs text-plum/50 underline-offset-4 hover:underline hover:text-plum">
                    Payments &amp; refunds
                </Link>
            </div>

            <div className="card-cloud divide-y divide-plum/5 overflow-hidden">
                {orders.map((o) => {
                    const st = STATE[o.status] || { label: o.status, tone: "text-plum/50", icon: Clock };
                    const Icon = st.icon;
                    const hardcover = o.kind === "hardcover";
                    // A plan has no book of its own yet — that is what the year makes.
                    const plan = o.kind === "annual";
                    return (
                        <div key={o.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 p-5"
                            data-testid={`order-row-${o.id}`}>
                            <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
                                hardcover ? "bg-sky/20" : "bg-lavender/40"}`}>
                                {plan ? <CalendarHeart className="w-4 h-4 text-plum/60" />
                                      : hardcover ? <Truck className="w-4 h-4 text-plum/60" />
                                                  : <BookHeart className="w-4 h-4 text-plum/60" />}
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-plum truncate">
                                    {plan ? "The Year Book" : (o.book_title || "Your storybook")}
                                </div>
                                <div className="text-xs text-plum/50">
                                    {plan ? "Weekly questions · hardcover at the end"
                                          : hardcover ? "Printed hardcover" : "Digital storybook"}
                                    {o.created_at ? ` · ${when(o.created_at)}` : ""}
                                </div>
                                {/* The same reference their emails quote, so support
                                    questions can name one order. */}
                                <div className="text-[11px] font-mono text-plum/40 mt-0.5"
                                    data-testid={`order-ref-${o.id}`}>
                                    {o.ref || o.id.slice(0, 8).toUpperCase()}
                                </div>
                            </div>

                            <div className="text-right">
                                <div className="font-display text-lg text-plum leading-none">
                                    ₹{(o.amount / 100).toFixed(0)}
                                </div>
                                <div className={`text-xs mt-1 flex items-center gap-1 justify-end ${st.tone}`}>
                                    <Icon className="w-3 h-3" /> {st.label}
                                </div>
                            </div>

                            {o.status_note && (
                                <div className="text-xs text-plum/50 basis-full"
                                    data-testid={`order-note-${o.id}`}>
                                    {o.status_note}
                                    {o.tracking ? ` · Post reference ${o.tracking}` : ""}
                                </div>
                            )}
                            {o.payment_issue && o.status !== "refunded" && (
                                <span className="text-xs text-plum/50 flex items-center gap-1 basis-full sm:basis-auto">
                                    <AlertCircle className="w-3 h-3" /> We're tracing this
                                </span>
                            )}
                            {canReportIssue(o) && (
                                <Button size="sm" variant="ghost" disabled={busy === o.id}
                                    className="text-xs text-plum/50 hover:text-plum basis-full sm:basis-auto"
                                    onClick={() => report(o)} data-testid={`payment-issue-${o.id}`}>
                                    Paid but not confirmed?
                                </Button>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
